import React, { useEffect, useState } from 'react';
import { Save, ExternalLink, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { adminGetLegalPages, adminUpdateLegalPage } from '../api/api';

const CHEMINS_PUBLICS = {
  'mentions-legales': '/mentions-legales',
  confidentialite: '/confidentialite',
  cgv: '/cgv',
};

export const AdminLegal = () => {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [brouillon, setBrouillon] = useState({ title: '', content: '' });
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    adminGetLegalPages()
      .then((data) => {
        setPages(data);
        if (data.length) {
          setActive(data[0].slug);
          setBrouillon({ title: data[0].title, content: data[0].content });
        }
      })
      .catch(() => toast.error('Chargement des pages légales impossible.'))
      .finally(() => setChargement(false));
  }, []);

  const changerPage = (slug) => {
    const page = pages.find((p) => p.slug === slug);
    if (!page) return;
    setActive(slug);
    setBrouillon({ title: page.title, content: page.content });
  };

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const maj = await adminUpdateLegalPage(active, brouillon);
      setPages((prev) => prev.map((p) => (p.slug === active ? maj : p)));
      toast.success('Page enregistrée.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setEnregistrement(false);
    }
  };

  const aCompleter = (brouillon.content.match(/\[[^\]]+\]/g) || []).length;

  if (chargement) {
    return <div className="p-8 text-gray-500">Chargement…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pages légales</h1>
        <p className="text-gray-600 mt-1">
          Mentions légales, politique de confidentialité et conditions de vente,
          affichées dans le pied de page du site.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {pages.map((page) => (
          <button
            key={page.slug}
            type="button"
            onClick={() => changerPage(page.slug)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              active === page.slug
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-600'
            }`}
          >
            {page.title}
          </button>
        ))}
      </div>

      {aCompleter > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">
              {aCompleter} élément{aCompleter > 1 ? 's' : ''} à compléter
            </p>
            <p className="mt-1">
              Les passages entre crochets, comme [Dénomination sociale], sont des
              emplacements à remplacer par vos informations réelles avant la mise en ligne.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Contenu</CardTitle>
          {CHEMINS_PUBLICS[active] && (
            <a
              href={CHEMINS_PUBLICS[active]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
            >
              Voir la page publique
              <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          )}
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="titre">Titre</Label>
            <Input
              id="titre"
              value={brouillon.title}
              onChange={(e) => setBrouillon((b) => ({ ...b, title: e.target.value }))}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contenu">Texte</Label>
            <Textarea
              id="contenu"
              value={brouillon.content}
              onChange={(e) => setBrouillon((b) => ({ ...b, content: e.target.value }))}
              rows={26}
              className="font-mono text-sm leading-relaxed"
            />
            <p className="text-xs text-gray-500">
              Mise en forme : <code>## Titre</code> pour une section,
              <code className="ml-1">**gras**</code>, <code className="ml-1">- élément</code> pour
              une liste. Une ligne vide sépare deux paragraphes.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-gray-500">
              Ces textes sont des trames. Faites-les relire par un juriste avant la mise en ligne.
            </p>
            <Button onClick={enregistrer} disabled={enregistrement} className="gap-2">
              <Save className="w-4 h-4" aria-hidden="true" />
              {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLegal;
